import { Button, Input, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, Textarea } from '@invent/wl-ui-kit-next/ui';
import { Paperclip, X } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ReasonDialog,
  StatusChip
} from '@/design';
import { ApiError } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type { IMailReply, IMailSuppression } from '../../api/mail-log-contracts';
import { mailLogApi } from '../../api/mail-log-api';
import { LoadError, TableSkeleton, useAdminData } from '../../components/admin-page';
import { personHubPath } from '../../components/participant-link';
import { mailLogPath } from '../../paths';
import { fieldRefusal, refusalMessage } from '../../refusal';
import { sourceLabel } from './mail-words';

/**
 * The suppression list's changes (SR-523 stage 3, `mail.outreach.send`) — an address added by
 * staff, an active suppression cleared with a note — and a reply read in a drawer. Shared by the
 * Email log and the record's Activity.
 */

const ADDRESS = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;

/** The consent a clear stands for, said where it is asked. */
export const CLEAR_WARNING =
  'Clear it only when the person agreed to receive survey invitations, reminders and outreach again — say how in the note.';

export function SuppressDialog({
  open,
  onOpenChange,
  address: given = '',
  onDone
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The address to suppress, when the page already knows it. */
  address?: string;
  onDone: (address: string) => void;
}) {
  const id = React.useId();
  const [address, setAddress] = React.useState(given);
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  React.useEffect(() => {
    if (!open) return;
    setAddress(given);
    setNote('');
    setRefusal(null);
  }, [open, given]);

  const trimmed = address.trim();
  const problem = trimmed && !ADDRESS.test(trimmed) ? 'One plain address, like name@firm.com.' : null;
  const blocked = busy || !trimmed || Boolean(problem) || note.length > 500;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (blocked) return;
    setBusy(true);
    setRefusal(null);
    try {
      await mailLogApi.suppress(trimmed, note.trim());
      onDone(trimmed);
      onOpenChange(false);
    } catch (error) {
      setRefusal(
        error instanceof ApiError && error.status === 409
          ? 'This address is suppressed already.'
          : (fieldRefusal(error, 'address') ?? refusalMessage(error, 'The address could not be suppressed.'))
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent data-suppress-dialog="">
        <DialogHeader>
          <DialogTitle>Suppress an address</DialogTitle>
          <DialogDescription>Bulk email skips it from now on; emails about the person&apos;s own actions still reach it.</DialogDescription>
        </DialogHeader>
        <DialogBody as="form" id={`${id}-form`} onSubmit={(event) => void submit(event)} className="text-[0.8125rem]">
          <div className="grid gap-1">
            <label htmlFor={`${id}-address`} className="font-medium">
              Address
            </label>
            <Input
              id={`${id}-address`}
              autoFocus={!given}
              readOnly={Boolean(given)}
              value={address}
              aria-invalid={problem ? true : undefined}
              onChange={(event) => setAddress(event.target.value)}
              className="h-8"
            />
            {problem && <p className="text-xs text-destructive">{problem}</p>}
          </div>
          <div className="grid gap-1">
            <label htmlFor={`${id}-note`} className="font-medium">
              Why <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Textarea id={`${id}-note`} autoFocus={Boolean(given)} value={note} maxLength={500} rows={2} onChange={(event) => setNote(event.target.value)} />
          </div>
          {refusal && (
            <p role="alert" className="text-destructive">
              {refusal}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form={`${id}-form`} size="sm" disabled={blocked}>
            {busy ? 'Suppressing…' : 'Suppress'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Clearing takes a note — the person's consent, in words — and says what it means first. */
export function ClearSuppressionDialog({
  suppression,
  onClose,
  onDone
}: {
  suppression: Nullable<Pick<IMailSuppression, 'id' | 'address' | 'source'>>;
  onClose: () => void;
  onDone: (address: string) => void;
}) {
  const [kept, setKept] = React.useState(suppression);
  if (suppression && suppression !== kept) setKept(suppression);
  const shown = suppression ?? kept;
  return (
    <ReasonDialog
      open={suppression !== null}
      onOpenChange={(open) => !open && onClose()}
      title={`Clear the suppression of ${shown?.address ?? ''}`}
      description={
        <>
          {shown && <span className="block">Source: {sourceLabel(shown.source)}.</span>}
          <span className="mt-1 block font-medium text-[var(--color-deep-magenta)]">{CLEAR_WARNING}</span>
        </>
      }
      reasonLabel="How the person agreed"
      required
      max={500}
      confirmLabel="Clear"
      onConfirm={async (note) => {
        if (!shown) return null;
        try {
          await mailLogApi.clear(shown.id, note);
          onDone(shown.address);
          return null;
        } catch (error) {
          if (error instanceof ApiError && error.status === 409) {
            onDone(shown.address);
            return null;
          }
          return refusalMessage(error, 'The suppression could not be cleared.');
        }
      }}
    />
  );
}

/** A reply as survey-api kept it: its text part and the files' names — never their contents. */
export function ReplyDrawer({
  replyId,
  onClose,
  onReviewed
}: {
  replyId: Nullable<string>;
  onClose: () => void;
  /** An unmatched reply marked reviewed: the queue reads again. */
  onReviewed?: () => void;
}) {
  return (
    <Sheet open={replyId !== null} onOpenChange={(open) => !open && onClose()} modal={false}>
      <SheetContent
        side="right"
        className="w-[min(32rem,calc(100vw-2rem))] gap-0 p-0"
        onOpenAutoFocus={(event) => event.preventDefault()}
        data-reply-drawer=""
      >
        <SheetHeader className="px-5 pt-5 pb-3">
          <SheetTitle className="text-[0.9375rem] text-[var(--color-royal-blue)]">Reply</SheetTitle>
          <SheetDescription>Its text as received. Attachments are listed, not kept.</SheetDescription>
          <Button type="button" variant="ghost" size="icon" className="ml-auto size-8" aria-label="Close reply" onClick={onClose}>
            <X className="size-4" aria-hidden />
          </Button>
        </SheetHeader>
        {replyId && <ReplyBody replyId={replyId} onReviewed={onReviewed} />}
      </SheetContent>
    </Sheet>
  );
}

function ReplyBody({ replyId, onReviewed }: { replyId: string; onReviewed?: () => void }) {
  const read = useAdminData((signal) => mailLogApi.reply(replyId, signal), [replyId]);
  if (read.error) {
    return (
      <div className="px-5">
        <LoadError message={read.error} />
      </div>
    );
  }
  const reply = read.data;
  if (!reply) {
    return (
      <div className="px-5">
        <TableSkeleton rows={3} />
      </div>
    );
  }
  return (
    <div className="grid content-start gap-3 overflow-y-auto px-5 pb-5 text-[0.8125rem]">
      <dl className="grid grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-1">
        <dt className="text-muted-foreground">From</dt>
        <dd className="flex flex-wrap items-center gap-2 break-all">
          {reply.from}
          {!reply.fromMatches && <StatusChip tone="attention">Not the address it was sent to</StatusChip>}
        </dd>
        <dt className="text-muted-foreground">To</dt>
        <dd className="break-all">{reply.recipient}</dd>
        <dt className="text-muted-foreground">Received</dt>
        <dd>{formatDateTime(reply.receivedAt)}</dd>
        <dt className="text-muted-foreground">Subject</dt>
        <dd className="break-words">{reply.subject}</dd>
        {reply.participant && (
          <>
            <dt className="text-muted-foreground">Participant</dt>
            <dd>
              <Link to={personHubPath(reply.participant.id)} className="text-[var(--color-sapphire)] hover:underline">
                {reply.participant.name}
              </Link>
            </dd>
          </>
        )}
        {reply.campaign && (
          <>
            <dt className="text-muted-foreground">Campaign</dt>
            <dd>
              <Link to={mailLogPath({ tab: 'campaigns', campaign: reply.campaign.id })} className="text-[var(--color-sapphire)] hover:underline">
                {reply.campaign.name}
              </Link>
            </dd>
          </>
        )}
        {reply.spamScore !== null && reply.spamScore >= 5 && (
          <>
            <dt className="text-muted-foreground">Spam score</dt>
            <dd>
              <StatusChip tone="attention">{reply.spamScore}</StatusChip>
            </dd>
          </>
        )}
      </dl>
      {/* Plain text in a pre: nothing of the reply is rendered as HTML. */}
      <pre className="rounded-md border border-border bg-[color-mix(in_srgb,var(--color-slight-blue)_30%,white)] p-3 font-sans text-[0.8125rem] leading-relaxed whitespace-pre-wrap" data-reply-text="">
        {reply.text || '(no text)'}
      </pre>
      {reply.attachments.length > 0 && (
        <ul className="grid gap-1" aria-label="Attachments">
          {reply.attachments.map((attachment, index) => (
            <li key={`${attachment.name}-${index}`} className="flex items-center gap-1.5 text-muted-foreground">
              <Paperclip className="size-3.5" aria-hidden />
              <span className="break-all">{attachment.name}</span>
              <span className="text-xs">{Math.max(1, Math.round(attachment.size / 1024))} KB</span>
            </li>
          ))}
        </ul>
      )}
      {reply.unmatched && (
        <ReviewBlock
          reply={reply}
          onReviewed={async () => {
            await read.reload();
            onReviewed?.();
          }}
        />
      )}
    </div>
  );
}

/** An unmatched reply's review: who marked it and why, or the note and the press that do. */
function ReviewBlock({ reply, onReviewed }: { reply: IMailReply; onReviewed: () => Promise<void> }) {
  const id = React.useId();
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  if (reply.reviewedAt) {
    return (
      <p className="rounded-md border border-border px-3 py-2 text-muted-foreground" data-reply-reviewed="">
        Reviewed {formatDateTime(reply.reviewedAt)}
        {reply.reviewedBy && ` by ${reply.reviewedBy}`}
        {reply.reviewNote && <span className="mt-1 block break-words text-foreground">{reply.reviewNote}</span>}
      </p>
    );
  }
  const review = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      await mailLogApi.review(reply.id, note.trim());
      await onReviewed();
    } catch (error) {
      setRefusal(refusalMessage(error, 'The reply could not be marked reviewed.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-1.5 rounded-md border border-border p-3" data-reply-review="">
      <span className="font-medium">No email matched this reply</span>
      <label htmlFor={`${id}-note`} className="text-xs text-muted-foreground">
        What was done with it <span>(optional)</span>
      </label>
      <Textarea id={`${id}-note`} value={note} maxLength={500} rows={2} onChange={(event) => setNote(event.target.value)} />
      {refusal && (
        <p role="alert" className="text-xs text-destructive">
          {refusal}
        </p>
      )}
      <Button type="button" size="sm" className="justify-self-start" disabled={busy} onClick={() => void review()}>
        {busy ? 'Marking…' : 'Mark reviewed'}
      </Button>
    </div>
  );
}
